
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getIsVerified,
  setIsVerified as setIsVerifiedStorage,
  getIsFillProfile,
  setIsFillProfile as setIsFillProfileStorage,
  getHasFamilyChart,
  setHasFamilyChart as setHasFamilyChartStorage,
  getIsEmailRequired,
  setIsEmailRequired as setIsEmailRequiredStorage,
  getIsPhoneRequired,
  setIsPhoneRequired as setIsPhoneRequiredStorage
} from "./verificationStorage";

// axios
import { api } from "@/api/axios";

import { clearMedCardOwner } from "utils/medCardOwner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isVerified, setIsVerified] = useState(() => getIsVerified());

  const [isFillProfile, setIsFillProfile] = useState(() => getIsFillProfile());

  // Флаг, прикреплена ли почта у пользователя. true - не прикреплена. false - прикреплена
  const [isEmailRequired, setIsEmailRequired] = useState(() => getIsEmailRequired());
  // Флаг, показывать ли пользователю модалку для подтверждения почты. true - показывать. false - скрыть
  const [showModalEmailRequire, setShowModalEmailRequire] = useState(isEmailRequired);

  // Флаг необходимости подтверждения телефона.
  const [isPhoneRequired, setIsPhoneRequired] = useState(() => getIsPhoneRequired());

  // Флаг отображения модалки подтверждения телефона.
  const [showModalPhoneRequire, setShowModalPhoneRequire] = useState(isPhoneRequired);

  // Доработка 25. HAS_FAMILY_CHART из get_user_data - есть ли подтверждённая карта у кого-то
  // из членов семьи. Отдельное состояние, а не часть isVerified: см. hasMedCard ниже.
  const [hasFamilyChart, setHasFamilyChart] = useState(() => getHasFamilyChart());

  const [role, setRole] = useState('USER');

  const [isUserLoading, setUserLoading] = useState(true);

  const [notificationList, setNotificationList] = useState([]);
  const [messageNotify, setMessageNotify] = useState([]); // Про мои галочки в обратной связи

  const [totalQuantity, setTotalQuantity] = useState('');

  const [isShowWarning, setIsShowWarning] = useState(false);
  const [messageWarning, setMessageWarning] = useState({
    title: '',
    description: ''
  });

  // Загрузка данных пользователя. Может вызываться повторно
  // (например, после логина нового пользователя).
  const refreshUser = async () => {
    try {
      setUserLoading(true);
      const res = await api.post('/gates/data/get_user_data', {});
      const data = res.data;

      setIsVerified(!!data.USER_CONFIRMED);
      setIsFillProfile(!!data.PROFILE_SAVE);
      // Поля нет (бэкенд старее Доработки 25) - false, то есть прежнее поведение:
      // раздел "Медкарта" доступен только по своей карте.
      setHasFamilyChart(!!data.HAS_FAMILY_CHART);
      setRole(data.ROLE || 'USER');

      if (data.EMAIL) {
        localStorage.setItem('email', data.EMAIL);
      }

      if (data.LOGIN) {
        localStorage.setItem('login', data.LOGIN);
      }

    } catch(e) {
      console.error('Ошибка загрузки данных пользователя:', e);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    setIsVerifiedStorage(isVerified);
  }, [isVerified]);

  useEffect(() => {
    setIsFillProfileStorage(isFillProfile);
  }, [isFillProfile]);

  useEffect(() => {
    setHasFamilyChartStorage(hasFamilyChart);
  }, [hasFamilyChart]);

  useEffect(() => {
    setIsEmailRequiredStorage(isEmailRequired);
  }, [isEmailRequired]);

  useEffect(() => {
    setShowModalEmailRequire(isEmailRequired);
  }, [isEmailRequired]);

  useEffect(() => {
    setIsPhoneRequiredStorage(isPhoneRequired);
  }, [isPhoneRequired]);

  useEffect(() => {
    setShowModalPhoneRequire(isPhoneRequired);
  }, [isPhoneRequired]);

  const resetUserSession = () => {
    setIsVerified(false);
    setIsFillProfile(false);
    setHasFamilyChart(false);
    setRole('USER');
    setUserLoading(false);

    setIsEmailRequired(false);
    setShowModalEmailRequire(false);
    setIsPhoneRequired(false);
    setShowModalPhoneRequire(false);

    localStorage.removeItem('isVerified');
    localStorage.removeItem('isFillProfile');
    localStorage.removeItem('hasFamilyChart');
    localStorage.removeItem('email');
    localStorage.removeItem('login');

    localStorage.removeItem('isEmailRequired');
    localStorage.removeItem('isPhoneRequired');

    clearMedCardOwner();
  };

  const value = useMemo(
    () => ({
      isVerified,
      setIsVerified,

      isFillProfile,
      setIsFillProfile,

      hasFamilyChart,
      setHasFamilyChart,

      isEmailRequired,
      setIsEmailRequired,

      showModalEmailRequire,
      setShowModalEmailRequire,

      isPhoneRequired,
      setIsPhoneRequired,

      showModalPhoneRequire,
      setShowModalPhoneRequire,

      // Доработка 25. Доступен ли раздел "Медкарта": своя карта (isVerified) ЛИБО карта
      // кого-то из членов семьи. Намеренно отдельный признак, а не расширение isVerified:
      // isVerified - это "подтверждённая регистрация", и от него зависят совсем другие вещи
      // (расчёт цены MODE='FREE'/'RESERVATION', корзина и оплата, F.Doc, виджет amoCRM).
      // Если бы мы расширили его, неподтверждённый родитель получил бы по карте ребёнка ещё
      // и льготные цены с доступом к оплате - поэтому hasMedCard используется ТОЛЬКО для
      // видимости медкарты.
      hasMedCard: isVerified || hasFamilyChart,

      isShowWarning,
      setIsShowWarning,

      messageWarning,
      setMessageWarning,

      notificationList,
      setNotificationList,

      messageNotify,
      setMessageNotify,

      totalQuantity,
      setTotalQuantity,

      role,
      setRole,
      isUserLoading,
      refreshUser,

      resetUserSession
    }),
    [
      isVerified, isFillProfile, hasFamilyChart, isShowWarning,
      messageWarning, notificationList, messageNotify, totalQuantity,
      role, isUserLoading, isEmailRequired, showModalEmailRequire,
      isPhoneRequired, showModalPhoneRequire
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
